import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';

import {
  useTaskLinkKindsListQuery,
  useTaskLinkSearchListLazyQuery,
} from 'gqlhooks';
import { useDebouncedValue } from 'core/hooks';

export interface IPickedLink {
  kind: string;
  id: string;
  name: string;
}

interface ITaskLinkPickerProps {
  /** Что уже выбрано — рисуется чипами и остаётся в списке опций. */
  value: IPickedLink[];
  onChange: (next: IPickedLink[]) => void;
  disabled?: boolean;
  /** Ширина полей: карточка широкая, поповер фильтров узкий. */
  compact?: boolean;
  /**
   * Имя связи, известное вызывающему. Нужно фильтру: его состояние живёт в
   * URL, а туда влезает только `вид:id`. Без этого чип показывал «42», пока
   * крошка над списком показывала «PL-JS-21» — одно условие, две подписи.
   */
  resolveName?: (kind: string, id: string) => string;
}

/**
 * Выбор связанных сущностей: сперва ВИД, потом поиск (ТЗ 0064).
 *
 * Один компонент на два места — карточку задачи и фильтр ленты. Второй
 * экземпляр разошёлся бы с первым на первой же правке, а правила здесь
 * неочевидные:
 *
 * 🔴 Уже выбранные значения ОБЯЗАНЫ оставаться в списке опций, даже если
 * текущая выдача поиска их не содержит: иначе antd рисует в чипе сырой id
 * вместо имени. Та же правка уже сделана в конструкторе опросов.
 *
 * Единый «умный» поиск по всем видам отвергнут: имена совпадают у студентов,
 * преподавателей, операторов и кандидатов, а права и выборки у видов разные.
 *
 * Названия видов переводит ФРОНТ. Сервер решает, какие виды доступны роли, —
 * это его дело; но подписи он отдаёт через Django-переводы, и на стенде это
 * дало вперемешку «UMOWA», «COURSE» и «GRUPA».
 */
export const TaskLinkPicker = memo(
  ({
    value,
    onChange,
    disabled = false,
    compact = false,
    resolveName,
  }: ITaskLinkPickerProps) => {
    const { t } = useTranslation();
    const [kind, setKind] = useState('');
    const [search, setSearch] = useState('');
    const debounced = useDebouncedValue(search, 300);
    /**
     * Имена уже выбранного, пережившие круг через URL.
     *
     * 🔴 Состояние фильтра живёт в адресной строке, а туда влезает только
     * `вид:id` — имени там нет по определению. Значит после КАЖДОГО выбора
     * `value` возвращается без имени, и чип под курсором превращался в «42».
     * Крошка над списком при этом имя доставала из ленты — одно и то же
     * условие было подписано на экране двумя разными способами.
     */
    const [names, setNames] = useState<Record<string, string>>({});

    /**
     * Стартовый вид — тот, что уже выбран.
     *
     * ⚠️ Раньше `kind` начинался пустым, а поле поиска было заблокировано, пока
     * вид не выберут. Человек, открывший ссылку с фильтром, видел в поповере
     * ПУСТО: что именно отобрано — приходилось угадывать, а снять можно было
     * только крошкой. Один эффект на первое непустое значение; дальше человек
     * переключает вид сам, и мы ему не мешаем.
     */
    const seeded = useRef(false);
    useEffect(() => {
      if (seeded.current || kind || !value.length) return;
      seeded.current = true;
      setKind(value[0].kind);
    }, [value, kind]);

    const { data: kindsData } = useTaskLinkKindsListQuery();
    const kinds = useMemo(() => kindsData?.taskLinkKinds || [], [kindsData]);

    const [
      runSearch,
      { data: found, loading },
    ] = useTaskLinkSearchListLazyQuery({ fetchPolicy: 'no-cache' });

    // Запрос идёт по ЗАДЕРЖАННОМУ значению: печать «Ковальский» иначе стоила
    // бы девяти запросов вместо одного.
    useEffect(() => {
      if (kind) runSearch({ variables: { kind, query: debounced } });
    }, [kind, debounced, runSearch]);

    const label = useCallback(
      (key: string) => {
        const own = t(`pages.tasks.links.kind.${key}`);
        if (own !== `pages.tasks.links.kind.${key}`) return own;
        return kinds.find((row) => row.key === key)?.label || key;
      },
      [kinds, t],
    );

    /** Выбранные ЭТОГО вида — только они рисуются чипами в поле поиска. */
    const pickedHere = useMemo(() => value.filter((row) => row.kind === kind), [
      value,
      kind,
    ]);

    const nameOf = useCallback(
      (row: IPickedLink) =>
        names[`${row.kind}:${row.id}`] ||
        (resolveName && resolveName(row.kind, row.id)) ||
        row.name,
      [names, resolveName],
    );

    const options = useMemo(() => {
      // Пока запрос в пути, старую выдачу не показываем: `found` держит ответ
      // ПРЕДЫДУЩЕГО вида, и переключение «Студенты → Группы» на секунду
      // рисовало студентов под заголовком «Группы». Флагом «какой вид я
      // запросил» это не чинится — он поднимается на старте, а данные
      // приезжают потом; честный признак только `loading`.
      const rows = (loading ? [] : found?.taskLinkSearch || []).map((row) => ({
        value: String(row.objectId),
        label: row.hint ? `${row.name} · ${row.hint}` : row.name,
        name: row.name,
      }));
      const seen = new Set(rows.map((row) => row.value));
      return [
        ...rows,
        ...pickedHere
          .filter((row) => !seen.has(row.id))
          .map((row) => ({
            value: row.id,
            label: nameOf(row),
            name: nameOf(row),
          })),
      ];
    }, [found, loading, pickedHere, nameOf]);

    const onPick = useCallback(
      (ids: string[]) => {
        const others = value.filter((row) => row.kind !== kind);
        const next = ids.map((id) => {
          const option = options.find((row) => row.value === id);
          const already = pickedHere.find((row) => row.id === id);
          return {
            kind,
            id,
            name: option?.name || (already && nameOf(already)) || id,
          };
        });
        // Поле поиска очищаем сами: `searchValue` контролируемый, а rc-select
        // при выборе зовёт только свой внутренний сброс и `onSearch` не
        // дёргает — набранное «ковал» оставалось в поле, и добавить второго
        // человека было нельзя, не стерев вручную.
        setSearch('');
        // Запоминаем имена ЗДЕСЬ: обратно из URL они не вернутся.
        setNames((prev) => {
          const merged = { ...prev };
          next.forEach((row) => {
            merged[`${row.kind}:${row.id}`] = row.name;
          });
          return merged;
        });
        onChange([...others, ...next]);
      },
      [value, kind, options, pickedHere, nameOf, onChange],
    );

    return (
      <>
        <Select
          value={kind || undefined}
          style={{ width: compact ? '100%' : 200 }}
          disabled={disabled}
          placeholder={t('pages.tasks.links.pickKind')}
          onChange={(next) => {
            setKind(next);
            setSearch('');
          }}
        >
          {kinds.map((row) => (
            <Select.Option key={row.key} value={row.key}>
              {label(row.key)}
            </Select.Option>
          ))}
        </Select>
        <Select
          mode="multiple"
          style={{ flex: 1, minWidth: compact ? '100%' : 260 }}
          value={pickedHere.map((row) => ({
            value: row.id,
            label: nameOf(row),
          }))}
          labelInValue
          disabled={disabled || !kind}
          loading={loading}
          filterOption={false}
          searchValue={search}
          placeholder={t('pages.tasks.links.searchPlaceholder')}
          onSearch={setSearch}
          onChange={(rows) =>
            onPick((rows as Array<{ value: string }>).map((r) => r.value))
          }
        >
          {options.map((row) => (
            <Select.Option key={row.value} value={row.value}>
              {row.label}
            </Select.Option>
          ))}
        </Select>
      </>
    );
  },
);

TaskLinkPicker.displayName = 'TaskLinkPicker';
